import { regionCodes as configRegionCodes } from "appConfiguration";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { CancelButton } from "core/components/CancelButton";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import { ReactHookEmailInput } from "core/components/ReactHookFormComponents/ReactHookEmailInput";
import { ReactHookFormSelect } from "core/components/ReactHookFormComponents/ReactHookFormSelect";
import { ReactHookTextField } from "core/components/ReactHookFormComponents/ReactHookTextField";
import { ResetButton } from "core/components/ResetButton";
import { SubmitButton } from "core/components/SubmitButton";
import type { SelectOption, TFunction } from "core/entities";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { usePageLogging } from "core/hooks/usePageLogging";
import { useRegion } from "core/hooks/useRegion";
import { useToaster } from "core/hooks/useToaster";
import { useValidationErrorToaster } from "core/hooks/useValidationErrorToaster";
import { getBrand } from "core/utils/getBrand";
import { FavoriteButton } from "features/MyLinks/components";
import { useRouter } from "next/router";
import { type FieldErrorsImpl, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const regionCodes = [...configRegionCodes];

const defaultValues = {
  professionalEmail: "",
  personalEmail: "",
  firstName: "",
  lastName: "",
  jobTitle: "",
  operationType: "",
  companyName: "",
  telephoneNumber: "",
  city: "",
  province: "",
  country: "",
  comment: "",
};

/*
Technical Operations (Plumbing, Electrical, Reconstruction)

Cleaning and Decontamination

Administrative Support (Project Management, Insurance, Customer Service)

Logistics (Storage, Demolition)
*/

function getOperationTypeOptions(t: TFunction): SelectOption[] {
  const { brandTwoLetterCode } = getBrand();
  if (brandTwoLetterCode === "NR") {
    return [
      t(
        "operationType.nr.technicalOperations",
        "Technical Operations (Plumbing, Electrical, Reconstruction)"
      ),
      t("operationType.nr.cleaning", "Cleaning and Decontamination"),
      t(
        "operationType.nr.administrativeSupport",
        "Administrative Support (Project Management, Insurance, Customer Service)"
      ),
      t("operationType.nr.logistics", "Logistics (Storage, Demolition)"),
    ].map((label) => ({ label, value: label }));
  }
  return [
    t("operationType.shop"),
    t("operationType.ho"),
    t("operationType.other"),
  ].map((label) => ({ label, value: label }));
}

export function EmailRequestPage() {
  const { back } = useRouter();
  const { brandTwoLetterCode } = getBrand();
  const centrixFetch = useCentrixFetch();
  const { successToast, errorToast } = useToaster();
  const validationErrorToaster = useValidationErrorToaster();
  const formMethods = useForm({ defaultValues });
  const { isSubmitting } = formMethods.formState;
  const region = useRegion();
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:EmailRequest",
  });
  const { t: tBase } = useTranslation();
  const errorMessage = t("submitError");
  const successMessage = t("submitSuccess");
  usePageLogging({
    itemType: "Page",
    itemId: "email-request",
    componentName: "EmailRequestPage",
  });

  async function onValidSubmit(form: typeof defaultValues) {
    const emailRequestResponse = await centrixFetch({
      method: "post",
      path: "/api/app/email-requests",
      body: form,
    });
    if (!emailRequestResponse.ok) {
      errorToast(errorMessage);
      return;
    }
    successToast(successMessage);
    formMethods.reset(defaultValues);
  }

  function onInvalidSubmit(errors: FieldErrorsImpl<typeof defaultValues>) {
    validationErrorToaster(Object.keys(errors).map((name) => t(name)));
  }
  const operationTypeOptions = getOperationTypeOptions(t);
  const countryOptions = regionCodes
    .sort((a) => (a === region ? -1 : 1))
    .map((code) => ({
      value: tBase(`Region:RegionName.${code}`),
      label: tBase(`Region:RegionName.${code}`),
    }));
  return (
    <PageContainer maxWidth="md">
      <PageHeader
        title={t("pageTitle")}
        subTitle={t("description")}
        TitleSlot={
          <FavoriteButton
            itemId="email-request"
            itemType="Page"
            title={t("pageTitle")}
          />
        }
        RightSlot={
          <Button startIcon={<ChevronLeft />} variant="outlined" onClick={back}>
            {t("goBack")}
          </Button>
        }
      />
      <Stack
        gap={2}
        component="form"
        onSubmit={formMethods.handleSubmit(onValidSubmit, onInvalidSubmit)}
      >
        <ReactHookEmailInput
          control={formMethods.control}
          required
          name="professionalEmail"
          label={t("professionalEmail")}
        />
        <ReactHookTextField
          control={formMethods.control}
          name="firstName"
          label={t("firstName")}
          required
        />
        <ReactHookTextField
          control={formMethods.control}
          name="lastName"
          label={t("lastName")}
          required
        />
        <ReactHookTextField
          control={formMethods.control}
          name="jobTitle"
          label={t("jobTitle")}
          required
        />
        {brandTwoLetterCode !== "NR" && (
          <ReactHookFormSelect
            required
            control={formMethods.control}
            name="operationType"
            label={t("operationType")}
            options={operationTypeOptions}
          />
        )}
        <ReactHookTextField
          control={formMethods.control}
          name="companyName"
          label={t("companyName")}
          required
        />
        <ReactHookTextField
          control={formMethods.control}
          required
          name="city"
          label={t("city")}
        />
        <ReactHookTextField
          control={formMethods.control}
          required
          name="province"
          label={t("province")}
        />
        <ReactHookFormSelect
          control={formMethods.control}
          required
          name="country"
          label={t("country")}
          options={countryOptions}
        />

        <ReactHookEmailInput
          control={formMethods.control}
          name="personalEmail"
          required
          textFieldProps={{
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title={t("personalEmailTooltipTitle")}>
                    <InfoOutlined fontSize="small" />
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
          label={t("personalEmail")}
        />
        <ReactHookTextField
          control={formMethods.control}
          name="telephoneNumber"
          label={t("telephoneNumber")}
          required
        />
        <ReactHookTextField
          control={formMethods.control}
          name="comment"
          label={t("comment")}
          multiline
          minRows={6}
          maxRows={6}
        />
        <Stack direction="row" gap={2} justifyContent="flex-end">
          <CancelButton onClick={back} />
          <ResetButton
            onClick={() => {
              formMethods.reset(defaultValues);
            }}
          />
          <SubmitButton loading={isSubmitting} />
        </Stack>
      </Stack>
    </PageContainer>
  );
}
